import React, { useCallback, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { Button, Icon, Modal, Loader } from "semantic-ui-react";
import swal from "sweetalert";
import { toast } from "react-toastify";

import AdminMenu from "../../../../components/Admin/MenuDetalhes/AdminMenu";
import Dropzone from "../../../../components/Admin/Dropzone";
import { producaov2 as api } from "../../../../services/apis";
import { isAuthAdmin } from "../../../../services/auth";
import { datePattern } from "../../../../utils/Utils";

import {
  Container,
  Content,
  Title,
  TopBar,
  SearchInput,
  SemanticTable,
  Thumb,
  StatusBadge,
  ActionGroup,
  ActionButton,
  ModalForm,
  FieldRow,
  UploadRow,
  UploadBox,
  ActiveSwitch,
  EmptyState,
} from "./styles";

const emptyForm = {
  id: null,
  url: "",
  url_mobile: "",
  alt_image: "",
  url_image: "",
  url_image_mobile: "",
  active: 1,
};

const logSystem = async (texto) => {
  try {
    await api.post("system_logs", {
      acao: JSON.stringify({ local: "campanha", texto }),
    });
  } catch (_) {
    // ignora falha no audit log — não bloqueia operação
  }
};

const uploadToFileServer = async (file) => {
  const inputData = new FormData();
  inputData.append("file", file);
  inputData.append("path", "campaign/upload-admin");
  const response = await api.post("/upload", inputData);
  return response.data.url;
};

const Campanhas = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploadingDesktop, setUploadingDesktop] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const auth = await isAuthAdmin();
      if (!auth) {
        localStorage.setItem("redirect", "/admin/marketing/campanhas");
        window.location.href = "/login";
        return;
      }
      loadCampaigns();
    })();
  }, []);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("admin/campaign");
      setCampaigns(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      swal("Não foi possível carregar as campanhas", "", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const openCreateModal = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (camp) => {
    setForm({
      id: camp.id,
      url: camp.url || "",
      url_mobile: camp.url_mobile || "",
      alt_image: camp.alt_image || "",
      url_image: camp.url_image || "",
      url_image_mobile: camp.url_image_mobile || "",
      active: camp.active ? 1 : 0,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving || uploadingDesktop || uploadingMobile) return;
    setModalOpen(false);
    setForm(emptyForm);
  };

  const handleDropDesktop = async (file) => {
    if (!file) return;
    setUploadingDesktop(true);
    try {
      const url = await uploadToFileServer(file);
      setForm((prev) => ({ ...prev, url_image: url }));
      toast.success("Imagem desktop enviada", { autoClose: 2500 });
    } catch (err) {
      toast.error("Falha ao enviar imagem desktop", { autoClose: 4000 });
    } finally {
      setUploadingDesktop(false);
    }
  };

  const handleDropMobile = async (file) => {
    if (!file) return;
    setUploadingMobile(true);
    try {
      const url = await uploadToFileServer(file);
      setForm((prev) => ({ ...prev, url_image_mobile: url }));
      toast.success("Imagem mobile enviada", { autoClose: 2500 });
    } catch (err) {
      toast.error("Falha ao enviar imagem mobile", { autoClose: 4000 });
    } finally {
      setUploadingMobile(false);
    }
  };

  const removeDesktopImage = () =>
    setForm((prev) => ({ ...prev, url_image: "" }));

  const removeMobileImage = () =>
    setForm((prev) => ({ ...prev, url_image_mobile: "" }));

  const saveCampaign = async () => {
    if (!form.url || !form.url_image || !form.url_image_mobile) {
      swal(
        "Campos obrigatórios",
        "Informe URL de destino + envie imagem desktop e imagem mobile antes de salvar.",
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        url: form.url,
        url_mobile: form.url_mobile || form.url,
        alt_image: form.alt_image,
        url_image: form.url_image,
        url_image_mobile: form.url_image_mobile,
        active: form.active ? 1 : 0,
      };

      if (form.id) {
        await api.put(`/admin/campaign/${form.id}`, payload);
        await logSystem(`editou a campanha id=${form.id}`);
        toast.success("Campanha atualizada com sucesso", { autoClose: 3000 });
      } else {
        const { data } = await api.post(`/admin/campaign`, payload);
        await logSystem(`criou a campanha id=${data?.data?.id || ""}`);
        toast.success("Campanha criada com sucesso", { autoClose: 3000 });
      }

      setModalOpen(false);
      setForm(emptyForm);
      await loadCampaigns();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Tente novamente em instantes.";
      swal("Não foi possível salvar a campanha", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const activateCampaign = async (camp) => {
    const ok = await swal({
      title: "Ativar campanha",
      text: `"${camp.alt_image || `id ${camp.id}`}" passa a ser a única ativa no site. Confirmar?`,
      icon: "info",
      buttons: ["Cancelar", "Ativar"],
    });
    if (!ok) return;

    try {
      await api.post(`/admin/campaign/${camp.id}/activate`);
      await logSystem(`ativou a campanha id=${camp.id}`);
      toast.success("Campanha ativada", { autoClose: 3000 });
      await loadCampaigns();
    } catch (err) {
      swal("Não foi possível ativar", "", "error");
    }
  };

  const deleteCampaign = async (camp) => {
    const ok = await swal({
      title: "Excluir campanha?",
      text: `Isso remove "${camp.alt_image || `id ${camp.id}`}" do banco. Não dá pra desfazer.`,
      icon: "warning",
      buttons: ["Cancelar", "Excluir"],
      dangerMode: true,
    });
    if (!ok) return;

    try {
      await api.delete(`/admin/campaign/${camp.id}`);
      await logSystem(`removeu a campanha id=${camp.id}`);
      toast.success("Campanha removida", { autoClose: 3000 });
      await loadCampaigns();
    } catch (err) {
      swal("Não foi possível excluir", "", "error");
    }
  };

  const filtered = campaigns.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (c.alt_image || "").toLowerCase().includes(s) ||
      (c.url || "").toLowerCase().includes(s) ||
      String(c.id).includes(s)
    );
  });

  return (
    <Container>
      <AdminMenu />

      <Content>
        <Title>Campanha</Title>

        <TopBar>
          <SearchInput
            type="text"
            placeholder="Buscar por descrição, URL ou ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button primary onClick={openCreateModal}>
            <Icon name="plus" /> Criar nova campanha
          </Button>
        </TopBar>

        {loading ? (
          <Loader active inline="centered">
            Carregando campanhas...
          </Loader>
        ) : filtered.length === 0 ? (
          <EmptyState>
            Nenhuma campanha encontrada.
            {search && " Tente limpar o filtro de busca."}
          </EmptyState>
        ) : (
          <SemanticTable size="large" celled>
            <SemanticTable.Header>
              <SemanticTable.Row>
                <SemanticTable.HeaderCell>ID</SemanticTable.HeaderCell>
                <SemanticTable.HeaderCell>Imagem</SemanticTable.HeaderCell>
                <SemanticTable.HeaderCell>Descrição (alt)</SemanticTable.HeaderCell>
                <SemanticTable.HeaderCell>URL</SemanticTable.HeaderCell>
                <SemanticTable.HeaderCell>Cliques</SemanticTable.HeaderCell>
                <SemanticTable.HeaderCell>Status</SemanticTable.HeaderCell>
                <SemanticTable.HeaderCell>Criada</SemanticTable.HeaderCell>
                <SemanticTable.HeaderCell textAlign="center">
                  Ações
                </SemanticTable.HeaderCell>
              </SemanticTable.Row>
            </SemanticTable.Header>

            <SemanticTable.Body>
              {filtered.map((camp) => (
                <SemanticTable.Row key={camp.id}>
                  <SemanticTable.Cell>{camp.id}</SemanticTable.Cell>
                  <SemanticTable.Cell>
                    {camp.url_image ? (
                      <Thumb
                        src={camp.url_image}
                        alt={camp.alt_image || camp.title_campaign}
                      />
                    ) : (
                      <span style={{ color: "#bbb" }}>sem imagem</span>
                    )}
                  </SemanticTable.Cell>
                  <SemanticTable.Cell>
                    {camp.alt_image || (
                      <span style={{ color: "#bbb" }}>—</span>
                    )}
                  </SemanticTable.Cell>
                  <SemanticTable.Cell
                    style={{
                      maxWidth: 220,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <a
                      href={camp.url}
                      target="_blank"
                      rel="noreferrer"
                      title={camp.url}
                    >
                      {camp.url}
                    </a>
                  </SemanticTable.Cell>
                  <SemanticTable.Cell>{camp.clicks || 0}</SemanticTable.Cell>
                  <SemanticTable.Cell>
                    <StatusBadge active={!!camp.active}>
                      {camp.active ? "Ativa" : "Inativa"}
                    </StatusBadge>
                  </SemanticTable.Cell>
                  <SemanticTable.Cell>
                    {camp.data_sys ? datePattern(camp.data_sys) : "—"}
                  </SemanticTable.Cell>
                  <SemanticTable.Cell textAlign="center">
                    <ActionGroup>
                      {!camp.active && (
                        <ActionButton
                          intent="activate"
                          icon
                          title="Ativar (desativa as outras)"
                          onClick={() => activateCampaign(camp)}
                        >
                          <Icon name="check circle" />
                        </ActionButton>
                      )}
                      <ActionButton
                        intent="edit"
                        icon
                        title="Editar"
                        onClick={() => openEditModal(camp)}
                      >
                        <Icon name="edit" />
                      </ActionButton>
                      <ActionButton
                        intent="delete"
                        icon
                        title="Excluir"
                        onClick={() => deleteCampaign(camp)}
                      >
                        <Icon name="trash" />
                      </ActionButton>
                    </ActionGroup>
                  </SemanticTable.Cell>
                </SemanticTable.Row>
              ))}
            </SemanticTable.Body>
          </SemanticTable>
        )}
      </Content>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        closeOnDimmerClick={!saving && !uploadingDesktop && !uploadingMobile}
        size="large"
      >
        <Modal.Header>
          {form.id ? `Editar campanha #${form.id}` : "Criar nova campanha"}
        </Modal.Header>
        <Modal.Content scrolling>
          <ModalForm>
            <FieldRow>
              <label>URL de destino (desktop)</label>
              <input
                type="text"
                placeholder="https://voitto.com.br/..."
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
              />
            </FieldRow>

            <FieldRow>
              <label>URL de destino (mobile) — opcional</label>
              <input
                type="text"
                placeholder="Deixe em branco para usar a mesma URL desktop"
                value={form.url_mobile}
                onChange={(e) =>
                  setForm({ ...form, url_mobile: e.target.value })
                }
              />
            </FieldRow>

            <FieldRow>
              <label>Texto alternativo da imagem (acessibilidade)</label>
              <input
                type="text"
                placeholder="Descreva brevemente a arte do banner"
                value={form.alt_image}
                onChange={(e) =>
                  setForm({ ...form, alt_image: e.target.value })
                }
              />
            </FieldRow>

            <UploadRow>
              <UploadBox>
                <label>Imagem desktop</label>
                <span className="hint">
                  Recomendado: 1138 × 269 px (proporção ~4.23:1)
                </span>
                <Dropzone selectedFile="" onFileUploaded={handleDropDesktop} />
                {uploadingDesktop && (
                  <Loader active inline="centered" size="small">
                    Enviando imagem desktop...
                  </Loader>
                )}
                {form.url_image && !uploadingDesktop && (
                  <>
                    <img
                      className="preview"
                      src={form.url_image}
                      alt="preview desktop"
                    />
                    <Button
                      size="small"
                      basic
                      color="red"
                      onClick={removeDesktopImage}
                      type="button"
                    >
                      <Icon name="trash" /> Remover imagem
                    </Button>
                  </>
                )}
              </UploadBox>

              <UploadBox>
                <label>Imagem mobile</label>
                <span className="hint">
                  Recomendado: 320 × 166 px (proporção ~1.93:1)
                </span>
                <Dropzone selectedFile="" onFileUploaded={handleDropMobile} />
                {uploadingMobile && (
                  <Loader active inline="centered" size="small">
                    Enviando imagem mobile...
                  </Loader>
                )}
                {form.url_image_mobile && !uploadingMobile && (
                  <>
                    <img
                      className="preview"
                      src={form.url_image_mobile}
                      alt="preview mobile"
                    />
                    <Button
                      size="small"
                      basic
                      color="red"
                      onClick={removeMobileImage}
                      type="button"
                    >
                      <Icon name="trash" /> Remover imagem
                    </Button>
                  </>
                )}
              </UploadBox>
            </UploadRow>

            <ActiveSwitch>
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) =>
                  setForm({ ...form, active: e.target.checked ? 1 : 0 })
                }
              />
              Já deixar esta campanha ativa (desativa qualquer outra ativa)
            </ActiveSwitch>
          </ModalForm>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeModal} disabled={saving}>
            Cancelar
          </Button>
          <Button
            primary
            onClick={saveCampaign}
            disabled={saving || uploadingDesktop || uploadingMobile}
            loading={saving}
            type="button"
          >
            {form.id ? "Salvar alterações" : "Criar campanha"}
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default withRouter(Campanhas);
